import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, BarChart3, Users, Clock, TrendingUp, ChevronDown, ChevronUp, Coins } from 'lucide-react';
import { toast } from 'sonner';
import CreatorNav from '@/components/CreatorNav';
import CreatorTopMenuBar from '@/components/CreatorTopMenuBar';
import CreatorMobileBottomNav from '@/components/CreatorMobileBottomNav';
import UniversalFooter from '@/components/UniversalFooter';
import { BACKEND_URL } from '@/config/api';

// BACKEND_URL imported from centralized config
const API = `${BACKEND_URL}/api`;

const CreatorAnalyticsPage = () => {
  const { manuscriptId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [showTechnicalDetails, setShowTechnicalDetails] = useState(false);

  useEffect(() => {
    loadAnalytics();
  }, [manuscriptId]);

  const loadAnalytics = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`${API}/creator/analytics/${manuscriptId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error loading analytics:', error);
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return Math.round(num).toString();
  };

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white text-xl">Loading analytics...</div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white text-xl">No analytics data available</div>
      </div>
    );
  }

  // Prepare heatmap data for visualization
  const heatmapData = Object.entries(analytics.heatmap || {})
    .map(([chunkIndex, seconds]) => ({
      chunkIndex: parseInt(chunkIndex),
      seconds: seconds
    }))
    .sort((a, b) => a.chunkIndex - b.chunkIndex);

  const maxHeatmapValue = Math.max(...heatmapData.map(d => d.seconds), 1);

  return (
    <div className="min-h-screen bg-gray-900">
      <CreatorNav />
      <CreatorTopMenuBar />
      
      <div className="pb-20 md:pb-0">
        <div className="container mx-auto px-4 py-8 max-w-7xl">
        {/* Header */}
        <div className="mb-8">
          <Button
            onClick={() => navigate('/author-console')}
            variant="ghost"
            className="text-gray-300 hover:text-white mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Author Console
          </Button>
          
          <h1 className="text-4xl font-bold text-white mb-2">
            Analytics Dashboard
          </h1>
          <p className="text-xl text-gray-400">{analytics.manuscript_title}</p>
        </div>

        {/* Key Metrics Cards - Credits as Primary */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="bg-gradient-to-br from-purple-900/50 to-purple-800/30 border-purple-700">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-purple-300 flex items-center">
                <Coins className="w-4 h-4 mr-2" />
                Total Credits Generated
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-white">
                {(analytics.credits?.total ?? 0).toFixed(2)}
              </div>
              <div className="text-xs text-purple-400 mt-1">
                Active: {(analytics.credits?.active ?? 0).toFixed(2)} | Passive: {(analytics.credits?.passive ?? 0).toFixed(2)}
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gray-800 border-gray-700">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-gray-400 flex items-center">
                <Users className="w-4 h-4 mr-2" />
                Total Readers
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-white">
                {analytics.total_readers}
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gray-800 border-gray-700">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-gray-400 flex items-center">
                <Clock className="w-4 h-4 mr-2" />
                Total Listen Time
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-white">
                {formatTime(analytics.total_listen_seconds)}
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gray-800 border-gray-700">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-gray-400 flex items-center">
                <BarChart3 className="w-4 h-4 mr-2" />
                Avg Completion
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-white">
                {analytics.average_completion_percent}%
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Technical Details Toggle (AEU/PEU) */}
        <div className="mb-8">
          <button
            onClick={() => setShowTechnicalDetails(!showTechnicalDetails)}
            className="flex items-center gap-2 text-sm text-gray-400 hover:text-gray-300 transition-colors"
          >
            {showTechnicalDetails ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            Technical Details (AEU/PEU)
          </button>
          
          {showTechnicalDetails && (
            <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-gray-800/50 rounded-lg border border-gray-700">
              <div>
                <div className="text-xs text-gray-500 mb-1">Total Engagement Units</div>
                <div className="text-lg font-semibold text-gray-300">{formatNumber(analytics.total_engagement_units)}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500 mb-1">Passive EU (PEU)</div>
                <div className="text-lg font-semibold text-gray-300">{formatNumber(analytics.passive_engagement_units || 0)}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500 mb-1">Active EU (AEU)</div>
                <div className="text-lg font-semibold text-gray-300">{formatNumber(analytics.active_engagement_units || 0)}</div>
              </div>
              <div className="col-span-full text-xs text-gray-500 mt-2">
                Policy: {analytics.credits?.version_id || 'credits_v1'} (AEU×8, PEU×1)
              </div>
            </div>
          )}
        </div>

        {/* Secondary Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white">Words Consumed</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-teal-400">
                {formatNumber(analytics.total_words_consumed)}
              </div>
              <div className="text-sm text-gray-400 mt-1">
                Total words read across all readers
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gray-800 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white">Dwell Time</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-teal-400">
                {formatTime(analytics.total_dwell_time_seconds)}
              </div>
              <div className="text-sm text-gray-400 mt-1">
                Time with player open but inactive
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gray-800 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white">Avg Session</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-teal-400">
                {formatTime(analytics.average_session_duration)}
              </div>
              <div className="text-sm text-gray-400 mt-1">
                Average time per reading session
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Engagement Heatmap */}
        <Card className="bg-gray-800 border-gray-700 mb-8">
          <CardHeader>
            <CardTitle className="text-white">Engagement Heatmap</CardTitle>
            <p className="text-sm text-gray-400 mt-1">
              Shows which segments of your book readers engage with most
            </p>
          </CardHeader>
          <CardContent>
            {heatmapData.length > 0 ? (
              <div className="space-y-2">
                {heatmapData.map((data) => (
                  <div key={data.chunkIndex} className="flex items-center gap-3">
                    <div className="text-sm text-gray-400 w-20">
                      Chunk {data.chunkIndex}
                    </div>
                    <div className="flex-1 bg-gray-700 rounded-full h-6 overflow-hidden">
                      <div
                        className="bg-teal-500 h-full transition-all duration-300"
                        style={{
                          width: `${(data.seconds / maxHeatmapValue) * 100}%`
                        }}
                      />
                    </div>
                    <div className="text-sm text-gray-300 w-24 text-right">
                      {formatTime(data.seconds)}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-gray-400 text-center py-8">
                No engagement data yet
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top Replayed Segments */}
        <Card className="bg-gray-800 border-gray-700 mb-8">
          <CardHeader>
            <CardTitle className="text-white">Top 3 Most Replayed Segments</CardTitle>
            <p className="text-sm text-gray-400 mt-1">
              Segments readers replay most often
            </p>
          </CardHeader>
          <CardContent>
            {analytics.top_replayed_segments && analytics.top_replayed_segments.length > 0 ? (
              <div className="space-y-3">
                {analytics.top_replayed_segments.map((segment, index) => (
                  <div key={segment.chunk_index} className="flex items-center justify-between p-3 bg-gray-700 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="text-2xl font-bold text-teal-400">
                        #{index + 1}
                      </div>
                      <div>
                        <div className="text-white font-medium">
                          Chunk {segment.chunk_index}
                        </div>
                        <div className="text-sm text-gray-400">
                          Replayed {segment.replay_count} times
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-gray-400 text-center py-8">
                No replay data yet
              </div>
            )}
          </CardContent>
        </Card>

        {/* Drop-off Point */}
        {analytics.drop_off_point && (
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white">Drop-off Point</CardTitle>
              <p className="text-sm text-gray-400 mt-1">
                Segment with lowest engagement
              </p>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between p-4 bg-red-900/20 border border-red-500/30 rounded-lg">
                <div>
                  <div className="text-white font-medium">
                    Chunk {analytics.drop_off_point.chunk_index}
                  </div>
                  <div className="text-sm text-gray-400">
                    {formatTime(analytics.drop_off_point.engagement_seconds)} total engagement
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
        </div>
      </div>

      <CreatorMobileBottomNav />
      <UniversalFooter />
    </div>
  );
};

export default CreatorAnalyticsPage;
